import { CustomerSelect } from "@/components/molecules/customer-select";
import { MembersSelect } from "@/components/molecules/members-select";
import { ServiceSelect } from "@/components/molecules/service-select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  isAppointmentGone,
  useBookingErrorMessage,
} from "@/features/calendar/booking-errors";
import { BookingTargetField } from "@/features/calendar/components/booking-target-field";
import {
  useBookingTargets,
  useCreateEvent,
  useDeleteEvent,
  useUpdateEvent,
} from "@/features/calendar/hooks/use-events";
import type { CalendarEvent } from "@/features/calendar/types";
import {
  useClientMembers,
  useCurrentClient,
} from "@/features/clients/hooks/use-clients";
import { useService } from "@/features/services/hooks/use-services";
import { useTranslation } from "@/features/utils/i18n";
import { zodResolver } from "@hookform/resolvers/zod";
import { VideoCameraIcon } from "@phosphor-icons/react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const createEventSchema = (t: (key: string) => string) =>
  z
    .object({
      service_id: z.string().optional(),
      customer_id: z.string().optional(),
      member_ids: z.array(z.string()).optional(),
      summary: z.string().optional(),
      description: z.string().optional(),
      location: z.string().optional(),
      start_time: z.string(),
      end_time: z.string(),
      is_all_day: z.boolean(),
      attendees: z.string().optional(), // Comma-separated emails
      booking_target: z.string().optional(),
    })
    .refine(
      (data) => {
        // Summary is required only when no service is selected
        if (!data.service_id || data.service_id === "") {
          return data.summary && data.summary.trim().length > 0;
        }
        return true;
      },
      {
        message: t("form.titleRequired"),
        path: ["summary"],
      },
    );

type EventFormData = z.infer<ReturnType<typeof createEventSchema>>;

interface EventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: CalendarEvent | null;
  defaultStartTime?: number;
  defaultEndTime?: number;
}

export function EventDialog({
  open,
  onOpenChange,
  event,
  defaultStartTime,
  defaultEndTime,
}: EventDialogProps) {
  const { t } = useTranslation("calendar");
  const isEditMode = !!event;
  const isReadOnly = event?.is_primary === false;
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const deleteEvent = useDeleteEvent();
  // Only a new appointment picks where it is booked; an existing one stays where it is.
  const { data: bookingTargets } = useBookingTargets(open && !isEditMode);
  const showBookingTarget =
    !isEditMode && (bookingTargets?.targets.length || 0) > 1;

  const errorMessage = useBookingErrorMessage();

  // Fetch members to convert IDs to emails
  const clientId = useCurrentClient()?.client.id || "";
  const { data: members = [] } = useClientMembers(clientId);

  const form = useForm<EventFormData>({
    resolver: zodResolver(createEventSchema(t)),
    defaultValues: {
      service_id: "",
      customer_id: "",
      member_ids: [],
      summary: "",
      description: "",
      location: "",
      start_time: defaultStartTime
        ? new Date(defaultStartTime).toISOString().slice(0, 16)
        : new Date().toISOString().slice(0, 16),
      end_time: defaultEndTime
        ? new Date(defaultEndTime).toISOString().slice(0, 16)
        : new Date(Date.now() + 3600000).toISOString().slice(0, 16),
      is_all_day: false,
      attendees: "",
      booking_target: "",
    },
  });

  // The default target arrives after the dialog opens: select it unless the user
  // already picked one.
  useEffect(() => {
    if (!isEditMode && bookingTargets && !form.getValues("booking_target")) {
      form.setValue("booking_target", bookingTargets.default_target);
    }
  }, [bookingTargets, isEditMode, form]);

  // Watch service selection to auto-compute end time
  const selectedServiceId = form.watch("service_id");
  const startTime = form.watch("start_time");
  const durationRef = useRef(
    event?.end_time && event?.start_time
      ? event?.end_time - event?.start_time
      : 3600000,
  ); // Default 1 hour
  const { data: selectedService } = useService(selectedServiceId || "");

  // Format datetime for input
  const formatLocalDateTime = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Update form when event changes
  useEffect(() => {
    if (event) {
      // Convert timestamps to local datetime-local format
      const startDate = new Date(event.start_time);
      const endDate = new Date(event.end_time);
      durationRef.current = endDate.getTime() - startDate.getTime();

      // Note: API doesn't include user_id in attendees, so we keep all as email format
      const emailAttendees =
        event.attendees?.attendees?.map((a) => a.email).join(", ") || "";

      form.reset({
        service_id: event.service_id || "",
        customer_id: event.customer_id || "",
        member_ids: event.performer_ids || [],
        summary: event.summary,
        description: event.description || "",
        location: event.location || "",
        start_time: formatLocalDateTime(startDate),
        end_time: formatLocalDateTime(endDate),
        is_all_day: event.is_all_day,
        attendees: emailAttendees,
      });
    } else if (defaultStartTime && defaultEndTime) {
      const startDate = new Date(defaultStartTime);
      const endDate = new Date(defaultEndTime);

      form.reset({
        service_id: "",
        customer_id: "",
        member_ids: [],
        summary: "",
        description: "",
        location: "",
        start_time: formatLocalDateTime(startDate),
        end_time: formatLocalDateTime(endDate),
        is_all_day: false,
        attendees: "",
        booking_target: bookingTargets?.default_target || "",
      });
    }
  }, [event, defaultStartTime, defaultEndTime, form]);

  // Auto-compute end time when service is selected (only on service change)
  useEffect(() => {
    if (selectedService && selectedService.duration) {
      const serviceDuration = selectedService.duration * 60 * 1000;
      durationRef.current = serviceDuration;
      const startTimeValue = form.getValues("start_time");
      if (startTimeValue) {
        const startDate = new Date(startTimeValue);
        const endDate = new Date(startDate.getTime() + serviceDuration);
        form.setValue("end_time", formatLocalDateTime(endDate));
      }
    }
  }, [selectedService?.id]);

  // Update end time when start time changes (for service events)
  useEffect(() => {
    if (selectedService && startTime) {
      const startDate = new Date(startTime);
      const endDate = new Date(startDate.getTime() + durationRef.current);
      form.setValue("end_time", formatLocalDateTime(endDate));
    }
  }, [startTime, selectedService?.id]);

  const handleStartTimeBlur = () => {
    if (selectedService) return; // Service duration takes precedence

    const startTimeValue = form.getValues("start_time");
    if (startTimeValue) {
      const startDate = new Date(startTimeValue);
      const expectedEndDate = new Date(
        startDate.getTime() + durationRef.current,
      );
      form.setValue("end_time", formatLocalDateTime(expectedEndDate));
    }
  };

  const handleEndTimeBlur = () => {
    if (selectedService) return; // Service duration takes precedence

    const startTimeValue = form.getValues("start_time");
    const endTimeValue = form.getValues("end_time");
    if (startTimeValue && endTimeValue) {
      const startDate = new Date(startTimeValue);
      const endDate = new Date(endTimeValue);
      const duration = endDate.getTime() - startDate.getTime();
      if (duration < 0) {
        // If negative, fix start time to maintain duration
        const fixedStartDate = new Date(
          endDate.getTime() - durationRef.current,
        );
        form.setValue("start_time", formatLocalDateTime(fixedStartDate));
      } else {
        // Update the duration reference
        durationRef.current = duration;
      }
    }
  };

  const onSubmit = async (data: EventFormData) => {
    const startTimeMs = new Date(data.start_time).getTime();
    const endTimeMs = new Date(data.end_time).getTime();

    // Convert member IDs to email/name objects
    const memberAttendees = (data.member_ids || [])
      .map((userId) => {
        const member = members.find(
          (m) => !m.isPending && m.user && m.user.id === userId,
        );
        if (member && member.user) {
          return {
            email: member.user.email,
            name:
              member.membership?.display_name ||
              member.user.full_name ||
              undefined,
          };
        }
        return null;
      })
      .filter((a) => a !== null) as Array<{ email: string; name?: string }>;

    const emailAttendees: Array<{ email: string; name?: string }> =
      data.attendees
        ? data.attendees
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email)
            .map((email) => ({ email }))
        : [];

    const allAttendees = [...memberAttendees, ...emailAttendees];

    // Use service name as summary if service is selected, otherwise use form summary
    // Validation ensures summary is present when no service is selected
    const summary = (
      selectedService ? selectedService.name : data.summary
    ) as string;

    const eventData = {
      service_id: data.service_id || undefined,
      customer_id: data.customer_id || undefined,
      performer_ids: data.member_ids?.length
        ? data.member_ids?.filter(Boolean)
        : undefined,
      summary,
      description: data.description,
      location: data.location,
      start_time: startTimeMs,
      end_time: endTimeMs,
      is_all_day: selectedService ? false : data.is_all_day,
      attendees: allAttendees,
    };

    try {
      if (isEditMode) {
        await updateEvent.mutateAsync({
          eventId: event.id,
          data: eventData,
        });
        toast.success(t("messages.updateSuccess"));
      } else {
        await createEvent.mutateAsync({
          ...eventData,
          booking_target: data.booking_target || undefined,
        });
        toast.success(t("messages.createSuccess"));
      }
      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error(error);
      toast.error(
        errorMessage(
          error,
          isEditMode ? t("messages.updateError") : t("messages.createError"),
        ),
      );
      // The appointment is gone from the booking system and from here: nothing
      // left to edit.
      if (isAppointmentGone(error)) onOpenChange(false);
    }
  };

  const handleDelete = async () => {
    if (!event) return;

    try {
      await deleteEvent.mutateAsync(event.id);
      toast.success(t("messages.deleteSuccess"));
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast.error(errorMessage(error, t("messages.deleteError")));
      // Already cancelled in the booking system, and removed from here with it.
      if (isAppointmentGone(error)) onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isReadOnly
              ? t("event.view")
              : isEditMode
                ? t("event.edit")
                : t("event.new")}
            {/* The customer has not confirmed this slot yet — it may still free up. */}
            {event?.status === "tentative" && (
              <Badge variant="outline">{t("event.unconfirmed")}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {isReadOnly
              ? t("eventDialog.readOnlyDescription")
              : isEditMode
                ? t("eventDialog.editDescription")
                : t("eventDialog.newDescription")}
          </DialogDescription>
        </DialogHeader>

        {/* The meeting URL is generated for the booking and is not an editable field —
            it is shown as the one thing a team member actually needs from this dialog:
            a way in. */}
        {event?.conference?.url && (
          <a
            href={event.conference.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm font-medium text-primary hover:bg-muted"
          >
            <VideoCameraIcon weight="fill" className="size-4 shrink-0" />
            <span className="underline underline-offset-4">
              {t("event.joinVideo")}
            </span>
            <span className="truncate text-xs font-normal text-muted-foreground">
              {event.conference.url}
            </span>
          </a>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="service_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.service")}</FormLabel>
                  <FormControl>
                    <ServiceSelect
                      value={field.value || ""}
                      onChange={field.onChange}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customer_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.customer")}</FormLabel>
                  <FormControl>
                    <CustomerSelect
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder={t("form.placeholders.customer")}
                      disabled={isReadOnly}
                      allowNone={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="member_ids"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.members")}</FormLabel>
                  <FormControl>
                    <MembersSelect
                      value={field.value || []}
                      onChange={field.onChange}
                      multiple={true}
                      placeholder={t("form.placeholders.members")}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!selectedService && (
              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.title")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("form.placeholders.title")}
                        {...field}
                        disabled={isReadOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div
              className={
                selectedService
                  ? "grid grid-cols-1 gap-4"
                  : "grid grid-cols-2 gap-4"
              }
            >
              <FormField
                control={form.control}
                name="start_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.startTime")}</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        disabled={isReadOnly}
                        onBlur={() => {
                          field.onBlur();
                          handleStartTimeBlur();
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!selectedService && (
                <FormField
                  control={form.control}
                  name="end_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.endTime")}</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          {...field}
                          disabled={isReadOnly}
                          onBlur={() => {
                            field.onBlur();
                            handleEndTimeBlur();
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {!selectedService && (
              <FormField
                control={form.control}
                name="is_all_day"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isReadOnly}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0">
                      {t("form.allDayEvent")}
                    </FormLabel>
                  </FormItem>
                )}
              />
            )}

            <Accordion type="single" collapsible>
              <AccordionItem value="advanced">
                <AccordionTrigger>{t("form.advanced")}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {showBookingTarget && (
                      <FormField
                        control={form.control}
                        name="booking_target"
                        render={({ field }) => (
                          <BookingTargetField
                            targets={bookingTargets?.targets ?? []}
                            value={field.value || ""}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    )}

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("form.notes")}</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={t("form.placeholders.notes")}
                              {...field}
                              disabled={isReadOnly}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="location"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("form.location")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("form.placeholders.location")}
                              {...field}
                              disabled={isReadOnly}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="attendees"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("form.additionalAttendees")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("form.placeholders.attendees")}
                              {...field}
                              disabled={isReadOnly}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <DialogFooter className="gap-2">
              {isReadOnly ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("event.close")}
                </Button>
              ) : (
                <>
                  {isEditMode && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={deleteEvent.isPending}
                      confirm={t("eventDialog.deleteConfirm")}
                    >
                      {t("event.delete")}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                  >
                    {t("event.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={createEvent.isPending || updateEvent.isPending}
                  >
                    {isEditMode ? t("event.update") : t("event.create")}
                  </Button>
                </>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
